import { type RefObject } from "react";
import { styled } from "@pigment-css/react";
import { PencilLine } from "@acto/web-common/components";
import { useTranslations } from "@acto/web-common/locales";

const InputCard = styled("div")<{ grow?: boolean; recording?: boolean }>(({ theme }) => ({
  width: "100%",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  padding: `${theme.fieldSalesTwo.spacing[24]} ${theme.fieldSalesTwo.spacing[18]}`,
  background: theme.fieldSalesTwo.colors.surface.glass,
  border: `1px solid ${theme.fieldSalesTwo.colors.surface.glassBorder}`,
  borderRadius: theme.fieldSalesTwo.radius[32],
  variants: [
    { props: { grow: true }, style: { flex: 1 } },
    { props: { grow: false }, style: { minHeight: 200 } },
    // While recording the card visually disappears — the transcript floats on the
    // page background. Padding/layout/typography stay identical so text doesn't jump.
    {
      props: { recording: true },
      style: { background: "transparent", border: "1px solid transparent" },
    },
  ],
}));

const Placeholder = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.fieldSalesTwo.spacing[10],
  // Fill the whole card so the entire input area is the click target, not just the
  // text row; the icon + label stay pinned top-left.
  flex: 1,
  width: "100%",
  padding: 0,
  border: "none",
  background: "transparent",
  cursor: "text",
  textAlign: "left",
  color: theme.fieldSalesTwo.colors.neutral[700],
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[16],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
}));

const TextArea = styled("textarea")(({ theme }) => ({
  flex: 1,
  width: "100%",
  minHeight: "9rem",
  resize: "none",
  border: "none",
  background: "transparent",
  outline: "none",
  padding: `0 0 0 ${theme.fieldSalesTwo.spacing[8]}`,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[16],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[900],
  "&::placeholder": { color: theme.fieldSalesTwo.colors.neutral[700] },
}));

interface WritingFieldProps {
  writing: boolean;
  transcript: string;
  /** Lock manual edits while a recording is appending live transcript. */
  readOnly: boolean;
  /** While recording, the input loses its card background and looks unfocused. */
  recording: boolean;
  onChangeTranscript: (value: string) => void;
  onEnterWriting: () => void;
  inputRef: RefObject<HTMLDivElement | null>;
}

export function WritingField({
  writing,
  transcript,
  readOnly,
  recording,
  onChangeTranscript,
  onEnterWriting,
  inputRef,
}: WritingFieldProps) {
  const t = useTranslations("fieldSalesTwo.recaps");

  return (
    <InputCard grow={writing} recording={recording} ref={inputRef}>
      {writing ? (
        <TextArea
          autoFocus
          placeholder={t("compose.placeholder")}
          value={transcript}
          readOnly={readOnly}
          onChange={(e) => onChangeTranscript(e.target.value)}
        />
      ) : (
        <Placeholder type="button" onClick={onEnterWriting}>
          <PencilLine width={18} height={18} />
          {t("compose.placeholder")}
        </Placeholder>
      )}
    </InputCard>
  );
}
